
import Vue from "vue";
import moment from "moment";
import {
  getOrderPaymentSummary,
  OrderPaymentSummary,
} from "@/utils/orderPaymentSummary";
import { summarizeRefundAmounts } from "@/utils/refundAmountSummary";
import {
  getOrderStatusText,
  getOrderStatusCode,
  getOrderStatusBadgeClass,
} from "@/utils/orderStatus";
import { getNaverPoint, isNaverPayOrder } from "@/utils/orderPathFilter";
import {
  getDetailedPaymentMethods,
  getOrderClaimDetails,
  PaymentMethodDetail,
  OrderClaimDetails,
} from "@/utils/orderClaimDetails";

export interface SourceItem {
  key: string;
  label: string;
  amount: number;
}

export default Vue.extend({
  name: "OrderInlineDetail",
  props: {
    item: {
      type: Object,
      required: true,
    },
    row: {
      type: Object,
      default: undefined,
    },
    hasBaseDomain: {
      type: Boolean,
      default: false,
    },
    showNaverPoint: {
      type: Boolean,
      default: false,
    },
  },
  filters: {
    formatDate(date: string): string {
      if (!date) return "";
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    },
  },
  computed: {
    isNaverPay(): boolean {
      return isNaverPayOrder(this.item);
    },
    naverPoint(): number {
      return getNaverPoint(this.item);
    },
    shouldShowNaverPoint(): boolean {
      return this.showNaverPoint && this.isNaverPay;
    },
    isRefund(): boolean {
      return Array.isArray(this.item?.refund_methods);
    },
    itemOrderId(): string {
      return String(this.item?.order_id || "-");
    },
    orderPlaceText(): string {
      return String(
        this.item?.order_place_name || this.item?.order?.order_place_name || ""
      ).trim();
    },
    orderDateText(): string {
      const date = this.isRefund
        ? this.item?.refund_date || this.item?.order_date
        : this.item?.payment_date || this.item?.order_date;
      return date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "-";
    },
    itemOrderDateText(): string {
      const date = this.item?.order_date || this.item?.order?.order_date;
      return date ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "";
    },
    hasOrderDateDiff(): boolean {
      const orderDate = this.item?.order_date || this.item?.order?.order_date;
      const paymentDate =
        this.item?.payment_date || this.item?.order?.payment_date;
      if (!orderDate || !paymentDate) return false;
      return (
        moment(orderDate).format("YYYY-MM-DD HH:mm:ss") !==
        moment(paymentDate).format("YYYY-MM-DD HH:mm:ss")
      );
    },
    orderMethodText(): string {
      if (this.isRefund) {
        if (!this.item?.refund_methods || this.item.refund_methods.length === 0)
          return "정보 없음";
        return this.item.refund_methods[0] == null
          ? "정보 없음"
          : this.item.refund_methods.toString();
      }
      return String(
        this.item?.payment_method_name ||
          this.item?.order?.payment_method_name ||
          "정보 없음"
      );
    },
    detailedPaymentMethods(): PaymentMethodDetail[] {
      return getDetailedPaymentMethods(this.item);
    },
    claimDetails(): OrderClaimDetails {
      return getOrderClaimDetails(this.item);
    },
    orderItems(): any[] {
      if (Array.isArray(this.item?.items) && this.item.items.length > 0) {
        return this.item.items;
      }
      if (
        Array.isArray(this.item?.order?.items) &&
        this.item.order.items.length > 0
      ) {
        return this.item.order.items;
      }
      return [];
    },
    paymentSummary(): OrderPaymentSummary {
      return getOrderPaymentSummary(this.item);
    },
    hasOrderAmountDiff(): boolean {
      const initial = this.paymentSummary.initialOrderCompositionAmount;
      const actual = this.paymentSummary.actualOrderCompositionAmount;
      const payment = this.paymentSummary.actualPaymentAmount;
      return initial !== actual || payment !== initial;
    },
    paymentDiffAmount(): number {
      return (
        this.paymentSummary.initialOrderCompositionAmount -
        this.paymentSummary.actualOrderCompositionAmount
      );
    },
    productSources(): SourceItem[] {
      const src =
        this.item?.initial_order_amount ||
        this.item?.order?.initial_order_amount ||
        {};
      const orderPrice = this.toNum(src.order_price_amount);
      const itemsTotal = this.orderItems.reduce((sum, item) => {
        const p = this.toNum(item.product_price);
        const q = this.toNum(item.quantity) || 1;
        const o = this.toNum(item.option_price);
        return sum + (p + o) * q;
      }, 0);

      const list: SourceItem[] = [];
      if (orderPrice > 0) {
        list.push({
          key: "order_price_amount",
          label: "주문서 상품가 합계",
          amount: orderPrice,
        });
      }
      if (itemsTotal > 0 && itemsTotal !== orderPrice) {
        list.push({
          key: "items_product_total",
          label: "품목 계산 합계",
          amount: itemsTotal,
        });
      }
      return list;
    },
    shippingSources(): SourceItem[] {
      const src =
        this.item?.initial_order_amount ||
        this.item?.order?.initial_order_amount ||
        {};
      const list: SourceItem[] = [
        {
          key: "shipping_fee",
          label: "기본 배송비",
          amount: Math.abs(this.toNum(src.shipping_fee)),
        },
        {
          key: "additional_shipping_fee",
          label: "추가 배송비",
          amount: Math.abs(this.toNum(src.additional_shipping_fee)),
        },
        {
          key: "international_shipping_insurance",
          label: "해외 배송 보험료",
          amount: Math.abs(this.toNum(src.international_shipping_insurance)),
        },
        {
          key: "additional_handling_fee",
          label: "취급 수수료",
          amount: Math.abs(this.toNum(src.additional_handling_fee)),
        },
      ];
      return list.filter((s) => s.amount > 0);
    },
    discountSources(): SourceItem[] {
      const src =
        this.item?.initial_order_amount ||
        this.item?.order?.initial_order_amount ||
        {};
      const list: SourceItem[] = [
        {
          key: "membership_discount_amount",
          label: "회원 등급 할인",
          amount: Math.abs(this.toNum(src.membership_discount_amount)),
        },
        {
          key: "shipping_fee_discount_amount",
          label: "배송비 할인",
          amount: Math.abs(this.toNum(src.shipping_fee_discount_amount)),
        },
        {
          key: "app_discount_amount",
          label: "모바일 앱 할인",
          amount: Math.abs(this.toNum(src.app_discount_amount)),
        },
        {
          key: "set_product_discount_amount",
          label: "세트 상품 할인",
          amount: Math.abs(this.toNum(src.set_product_discount_amount)),
        },
        {
          key: "market_other_discount_amount",
          label: "마켓/제휴 할인",
          amount: Math.abs(this.toNum(src.market_other_discount_amount)),
        },
        {
          key: "order_discount_amount",
          label: "주문 추가 할인",
          amount: Math.abs(this.toNum(src.order_discount_amount)),
        },
        {
          key: "item_discount_amount",
          label: "품목 개별 할인",
          amount: Math.abs(this.toNum(src.item_discount_amount)),
        },
      ];

      const itemAdditionalDiscount = this.orderItems.reduce(
        (sum, i) => sum + Math.abs(this.toNum(i.additional_discount_price)),
        0
      );
      if (itemAdditionalDiscount > 0) {
        list.push({
          key: "item_additional_discount",
          label: "품목 추가 할인",
          amount: itemAdditionalDiscount,
        });
      }

      const itemAppDiscount = this.orderItems.reduce(
        (sum, i) => sum + Math.abs(this.toNum(i.app_item_discount_amount)),
        0
      );
      if (itemAppDiscount > 0) {
        list.push({
          key: "item_app_discount",
          label: "품목 앱 할인",
          amount: itemAppDiscount,
        });
      }

      const itemMarketDiscount = this.orderItems.reduce(
        (sum, i) => sum + Math.abs(this.toNum(i.market_discount_amount)),
        0
      );
      if (itemMarketDiscount > 0) {
        list.push({
          key: "item_market_discount",
          label: "품목 마켓 할인",
          amount: itemMarketDiscount,
        });
      }

      return list.filter((s) => s.amount > 0);
    },
    couponSources(): SourceItem[] {
      const src =
        this.item?.initial_order_amount ||
        this.item?.order?.initial_order_amount ||
        {};
      const list: SourceItem[] = [
        {
          key: "coupon_discount_price",
          label: "주문/상품 쿠폰 할인",
          amount:
            Math.abs(this.toNum(src.coupon_discount_price)) ||
            Math.abs(this.toNum(src.coupon_discount_amount)),
        },
        {
          key: "coupon_shipping_fee_amount",
          label: "배송비 쿠폰 할인",
          amount: Math.abs(this.toNum(src.coupon_shipping_fee_amount)),
        },
      ];

      const itemCoupons = this.orderItems.reduce(
        (sum, i) =>
          sum +
          Math.abs(
            this.toNum(i.coupon_discount_price || i.coupon_discount_amount)
          ),
        0
      );
      if (itemCoupons > 0 && list.every((c) => c.amount === 0)) {
        list.push({
          key: "item_coupon_discount",
          label: "품목 쿠폰 할인",
          amount: itemCoupons,
        });
      }

      return list.filter((s) => s.amount > 0);
    },
    refundActualAmount(): number {
      return this.toNum(this.item?.actual_refund_amount);
    },
    refundPoints(): number {
      return this.toNum(this.item?.used_points);
    },
    refundCredits(): number {
      return this.toNum(this.item?.used_credits);
    },
    refundTotal(): number {
      return Math.abs(summarizeRefundAmounts([this.item]).amount);
    },
    totalQuantity(): number {
      return this.orderItems.reduce(
        (sum, item) => sum + (this.toNum(item?.quantity) || 1),
        0
      );
    },
    totalProductPrice(): number {
      return this.orderItems.reduce((sum, item) => {
        const price = this.toNum(item?.product_price);
        const qty = this.toNum(item?.quantity) || 1;
        return sum + price * qty;
      }, 0);
    },
    totalOptionPrice(): number {
      return this.orderItems.reduce((sum, item) => {
        const optPrice = this.toNum(item?.option_price);
        const qty = this.toNum(item?.quantity) || 1;
        return sum + optPrice * qty;
      }, 0);
    },
    totalItemAmount(): number {
      return this.orderItems.reduce((sum, item) => {
        const price = this.toNum(item?.product_price);
        const optPrice = this.toNum(item?.option_price);
        const qty = this.toNum(item?.quantity) || 1;
        return sum + Math.max(0, (price + optPrice) * qty);
      }, 0);
    },
  },
  methods: {
    toNum(val: unknown): number {
      if (val === null || val === undefined || val === "") return 0;
      if (typeof val === "number" && Number.isFinite(val)) return val;
      const parsed = Number(String(val).replace(/,/g, "").trim());
      return Number.isFinite(parsed) ? parsed : 0;
    },
    formatPrice(val: unknown): string {
      return Math.abs(this.toNum(val)).toLocaleString();
    },
    hasEmbeddedValue(val: unknown): boolean {
      return val !== null && val !== undefined && val !== "";
    },
    formatEmbeddedPrice(val: unknown): string {
      if (!this.hasEmbeddedValue(val)) return "";
      return Math.abs(this.toNum(val)).toLocaleString();
    },
    formatEmbeddedQuantity(val: unknown): string {
      if (!this.hasEmbeddedValue(val)) return "";
      return this.toNum(val).toLocaleString();
    },
    getItemOptionText(productItem: any): string {
      return productItem?.option_value == null
        ? ""
        : String(productItem.option_value);
    },
    calculateItemTotal(productItem: any): string {
      if (
        !this.hasEmbeddedValue(productItem?.product_price) ||
        !this.hasEmbeddedValue(productItem?.option_price) ||
        !this.hasEmbeddedValue(productItem?.quantity)
      ) {
        return "";
      }
      const productPrice = this.toNum(productItem.product_price);
      const optionPrice = this.toNum(productItem.option_price);
      const quantity = this.toNum(productItem.quantity);
      const total = Math.max(0, (productPrice + optionPrice) * quantity);
      return `${total.toLocaleString()}원`;
    },
    getItemStatusText(productItem: any): string {
      const statusCode = getOrderStatusCode(productItem);
      return statusCode ? getOrderStatusText(productItem) : "";
    },
    getItemStatusCode(productItem: any): string {
      return getOrderStatusCode(productItem);
    },
    getItemStatusBadgeClass(productItem: any): string {
      return getOrderStatusBadgeClass(productItem);
    },
    isItemCancelled(productItem: any): boolean {
      const code = getOrderStatusCode(productItem);
      return code.startsWith("C");
    },
    isItemReturned(productItem: any): boolean {
      const code = getOrderStatusCode(productItem);
      return code.startsWith("R");
    },
    isItemExchange(productItem: any): boolean {
      const code = getOrderStatusCode(productItem);
      return code.startsWith("E") || code === "N01";
    },
    getItemClaimQuantity(productItem: any): number | undefined {
      if (!this.hasEmbeddedValue(productItem?.claim_quantity)) return undefined;
      return this.toNum(productItem.claim_quantity);
    },
    getItemIndividualDiscount(productItem: any): number {
      return (
        this.toNum(productItem?.additional_discount_price) +
        this.toNum(productItem?.item_discount_amount) +
        this.toNum(productItem?.app_item_discount_amount)
      );
    },
  },
});
